<template>
    <v-app>
        <v-main>
            <app-bar v-if="displayAppBar" />
            <v-container fluid class="main">
                <v-row no-gutters class="main-row">
                    <!-- Left sidebar: visible on md+ only -->
                    <v-col
                        v-if="displayHeader"
                        cols="12"
                        xs="12"
                        sm="12"
                        md="5"
                        class="sidebar flex-column"
                    >
                        <organisation-header
                            :loading="!routerReady"
                            :logo-shortcode="cloudflareLogoShortcode"
                            :organisation-name="
                                orgStore.organisation?.organisationName
                            "
                            :payment-title="getPaymentTitle"
                            :show-logo="displayOrganisationLogo"
                            :on-logo-error="redirectTo404"
                        />
                    </v-col>

                    <!-- Main content -->
                    <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        :md="displayHeader ? 7 : 12"
                        :class="[
                            'main-content-col',
                            { 'main-content-col--no-sidebar': !displayHeader },
                        ]"
                    >
                        <div
                            :class="[
                                'main-content',
                                { 'main-content--centered': !displayHeader },
                            ]"
                        >
                            <!-- TODO update or replace the payment journey progress bar in https://esenda.atlassian.net/browse/ESD-2820-->
                            <!-- Commented this one out for now as it will be replaced in a future update -->
                            <!-- <payment-journey-progress-bar /> -->

                            <template v-if="routerReady">
                                <page-heading
                                    v-if="routePageHeading"
                                    class="mt-4 ml-4"
                                    :heading="routePageHeading"
                                    :subheading="routePageSubheading"
                                />

                                <router-view></router-view>

                                <core-alert
                                    v-model="alertConfiguration.visible"
                                    :alert-message="alertConfiguration.message"
                                    :alert-color="alertConfiguration.color"
                                    :internationalised-message="
                                        alertConfiguration.internationalisedMessage
                                    "
                                    :alert-parameters="
                                        alertConfiguration.alertParameters
                                    "
                                />
                            </template>

                            <div v-else>
                                <!-- TODO: improve for pages other than supp form -->
                                <v-skeleton-loader
                                    type="heading"
                                    width="55%"
                                    class="mb-4 mt-1"
                                />

                                <v-skeleton-loader
                                    v-for="n in 5"
                                    :key="n"
                                    height="50"
                                    type="ossein"
                                    class="my-8"
                                />

                                <v-divider
                                    class="mt-4 mb-8 negative-x-divider"
                                />

                                <v-skeleton-loader
                                    type="button"
                                    class="w-100 mb-2"
                                />
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </v-container>
        </v-main>

        <esenda-footer v-show="displayFooter">
            <template #links>
                <a @click.prevent="handleLinksRedirect('TermsAndConditions')">
                    Terms And Conditions
                </a>
                <a @click.prevent="handleLinksRedirect('PrivacyPolicy')">
                    Privacy Policy
                </a>
            </template>
        </esenda-footer>

        <!-- Global Dialogs -->
        <fx-quote-expired-dialog :pop-up-visible="showFxQuoteExpiredDialog" />
    </v-app>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, watch, defineAsyncComponent } from "vue";
import {
    useCheckoutStore,
    usePiniaStore,
    useSessionStore,
    useEMandatesStore,
    usePaymentsStore,
} from "@/plugins/store";
import { useRouter, useRoute } from "vue-router";

import appBar from "@/templates/appBar.vue";
import esendaFooter from "@/components/layout/EsendaFooter.vue";
import organisationHeader from "@/components/layout/organisationHeader.vue";
import pageHeading from "@/components/base/pageHeading.vue";

import { useAuthenticator } from "@aws-amplify/ui-vue";
import { setSentryUser } from "@/helpers/sentry";
import {
    extractShortcodeFromUrl,
    keysToCamelCase,
} from "@/helpers/stringProcessors";
import { getOrganisationData } from "@/apis/esendaV2/organisations";
import { handleLogout } from "@/helpers/session";
import { routeNames } from "@/constants/routeNames";
import { getEmailFromCurrentSession } from "@/helpers/auth";
import { isPartnerLink } from "@/helpers/organisation";
import { useHelpScoutBeacon } from "@/composables/useHelpScoutBeacon";
import { useAlert } from "@esenda/core-ui/composables";
import { CoreAlert } from "@esenda/core-ui/components";
import { useOrganisationStore } from "@/plugins/store";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";

/**
 * Define async component imports
 */
const fxQuoteExpiredDialog = defineAsyncComponent(
    () => import("@/components/group/fxQuoteExpiredDialog.vue"),
);

const pStore = usePiniaStore();
const sessionStore = useSessionStore();
const { sessionId, emailAddress } = storeToRefs(sessionStore);
const orgStore = useOrganisationStore();
const checkoutStore = useCheckoutStore();
const eMandatesStore = useEMandatesStore();
const paymentsStore = usePaymentsStore();
const router = useRouter();
const route = useRoute();
const auth = useAuthenticator();
const isAuthenticated = ref(auth.authStatus === "authenticated");
const { showAlert, alertConfiguration } = useAlert();
const { t } = useI18n();
const { smAndUp } = useDisplay();

const routerReady = ref(false);

const shortcode = computed(() => route.params.shortcode);
const formName = computed(() => route.params.form);
const accountAlias = computed(() =>
    route.params.accountAlias ? String(route.params.accountAlias) : null,
);

const routePageHeading = computed(() =>
    route.meta?.headingKey ? t(route.meta?.headingKey as string) : undefined,
);

const routePageSubheading = computed(() =>
    route.meta?.subheadingKey
        ? t(route.meta?.subheadingKey as string)
        : undefined,
);

/** Load helpscout support widget */
const beaconId = import.meta.env.VITE_HELPSCOUT_BEACON_ID;
useHelpScoutBeacon(beaconId);

/** Shortcodes not associated to an organisation */
const shortcodeExceptions = ref([
    "",
    "unauthorised",
    "lloyds-3ds-challenge-iframe",
    "lloyds-3ds-method-form-iframe",
    "terms-and-conditions",
    "privacy-policy",
    "reeds",
]);
/** Routes where we don't want to display the organisation logo */
const getImageRouteExceptions = ref([
    routeNames.DEFAULT,
    routeNames.NOT_FOUND,
    routeNames.ACCESS_DENIED,
    routeNames.TERMS_AND_CONDITIONS,
    routeNames.PRIVACY_POLICY,
    routeNames.REEDS_LANDING_PAGE,
    routeNames.LLOYDS_CHALLENGE_IFRAME,
    routeNames.LLOYDS_METHOD_FORM_IFRAME,
]);

const showFxQuoteExpiredDialog = computed(() => {
    if (!smAndUp.value || route.name !== routeNames.PAYMENT_METHOD_SELECTION) {
        return false;
    }
    const expired = checkoutStore.expiredQuote;
    if (!expired) return false;
    return (
        expired.provider ===
        paymentsStore.selectedProviderDetails?.paymentMethod
    );
});

const getShortcodeForCloudFlare = () => {
    /**
     * Extracts the shortcode from window.location.href.
     * Handles redirection case, where it updates the auth
     * shortcode with the stored shortcode
     */
    const shortcode = extractShortcodeFromUrl(window.location.href);
    if (shortcode === "auth") {
        return String(orgStore.shortcode);
    } else {
        return shortcode;
    }
};

const cloudflareLogoShortcode = ref(getShortcodeForCloudFlare());

const redirectTo404 = () => {
    /**
     * Redirects the user to the 404 page. If the shortcode is in
     * the list of exceptions we abort the function. Otherwise,
     * we redirect to 404.
     */
    if (shortcodeExceptions.value.includes(cloudflareLogoShortcode.value)) {
        return;
    }

    router.push({ name: "404" });
};

const handleLinksRedirect = (viewName: string) => {
    /**
     * Opens a new tab to show the view corresponding on the link he clicked.
     * @param {string} viewName
     */
    const routeData = router.resolve({ name: viewName });
    window.open(routeData.href, "_blank");
};

const getPaymentTitle = computed(() => {
    /**
     * Returns eMandates as title if the url includes e-mandates.
     * Returns the payment title to be displayed below the organisation logo.
     * If form name is "partner_link" it returns the description
     * property from the supplementaryData object as the title, otherwise it
     * returns the paymentRequestReference from the form structure.
     */
    if (
        isPartnerLink() &&
        checkoutStore.supplementaryData &&
        checkoutStore.supplementaryData.description
    ) {
        return checkoutStore.supplementaryData.description;
    } else if (window.location.href.includes("e-mandates")) {
        return "Setup Electronic Mandates";
    } else {
        const formName =
            orgStore.formStructure?.paymentRequestReference ??
            orgStore.formName;
        return `${formName} payment `;
    }
});

/**
 * Hides the app bar for the Lloyds 3ds flow
 */
const displayAppBar = computed(() => {
    const routeName = route.name as routeNames;
    const isExceptionRoute = [
        routeNames.LLOYDS_CHALLENGE_IFRAME,
        routeNames.LLOYDS_METHOD_FORM_IFRAME,
    ].includes(routeName);
    return !isExceptionRoute;
});

const displayHeader = computed(() => {
    /**
     * Hides the organisation logo header if the current route is in the
     * list of route exceptions.
     */
    const routeName = route.name as routeNames;
    const isAuthRoute = [
        routeNames.AUTH_REDIRECT,
        routeNames.AUTH_SIGN_OUT,
    ].includes(routeName);
    const isExceptionRoute = getImageRouteExceptions.value.includes(routeName);
    return !isExceptionRoute && !isAuthRoute;
});

const displayOrganisationLogo = computed(() => {
    /**
     * Displays the organisation logo on all routes except the route exceptions.
     * @returns {boolean}
     */
    const routeName = route.name as routeNames;
    return !getImageRouteExceptions.value.includes(routeName);
});

const displayFooter = computed(() => {
    return ![
        routeNames.LLOYDS_CHALLENGE_IFRAME,
        routeNames.LLOYDS_METHOD_FORM_IFRAME,
    ].includes(route.name as routeNames);
});

const endSession = () => {
    /**
     * Clears the session data and redirects logs
     * out the user
     * @return {[void]}
     */
    const shortcodeParam =
        shortcode.value === undefined || shortcode.value === null
            ? orgStore.shortcode
            : shortcode.value;
    const formNameParam =
        formName.value === undefined || formName.value === null
            ? orgStore.formName
            : formName.value;

    pStore.clearSessionData();

    if (isAuthenticated.value) {
        handleLogout();
    } else {
        let redirectTo = "Invoice";
        const pathParams = { shortcode: shortcodeParam };

        if (formNameParam !== "invoice") {
            redirectTo = "Form";
            pathParams["form"] = formNameParam;
        }

        if (
            route.name !== redirectTo &&
            !getImageRouteExceptions.value.includes(route.name as routeNames)
        ) {
            showAlert("alerts.sessionTimeout");
            router.push({ name: redirectTo, params: pathParams });
        }
    }
};

watch(
    auth,
    async () => {
        isAuthenticated.value = auth.authStatus === "authenticated";
        sessionStore.setIsAuthenticated(isAuthenticated.value);

        if (isAuthenticated.value) {
            // get user details from Cognito
            const email = await getEmailFromCurrentSession();
            sessionStore.setEmailAddress(email as string);
        }
    },
    { immediate: true },
);

const detectIdleSession = () => {
    /**
     * Detects period of user inactivity and resets session
     * after 2h of inactivity
     * @return {[void]}
     * */

    let time;
    window.onload = resetTimer;

    // DOM Events
    document.onmousemove = resetTimer;
    document.onkeydown = resetTimer;
    document.onmousedown = resetTimer; // catches touchscreen presses as well
    document.ontouchstart = resetTimer; // catches touchscreen swipes as well
    document.ontouchmove = resetTimer; // required by some devices
    document.onclick = resetTimer; // catches touchpad clicks as well

    function resetTimer() {
        clearTimeout(time);
        // TODO ensure 2h is the right time
        time = setTimeout(endSession, 7200000);
    }
};

const getOrganisation = async () => {
    /**
     * Calls the organisation api. If the shortcode from the route is in the
     * list of exceptions we abort the function. If the api returns an organisation
     * we save it in the session storage. If it returns 404, we redirect the user
     * to the 404 page.
     * @returns {[void]}
     */
    const currentShortcode = String(shortcode.value);
    if (shortcodeExceptions.value.includes(currentShortcode)) {
        return;
    }

    await getOrganisationData(currentShortcode, String(orgStore.formName));
};

sessionStore.setDefaultCountryCode();
sessionStore.setDefaultLanguage();

onMounted(async () => {
    await router.isReady();
    routerReady.value = true;

    if (sessionId.value === null) {
        sessionStore.setSessionId();
    }

    const isAuthRoute = [
        routeNames.AUTH_REDIRECT,
        routeNames.AUTH_SIGN_OUT,
    ].includes(route.name as routeNames);
    if (!isAuthRoute) {
        eMandatesStore.setAccountAlias(accountAlias.value);
        orgStore.setFormName(String(formName.value));
    }

    setSentryUser(String(emailAddress.value), String(sessionId.value));

    if (shortcode.value !== undefined) {
        getOrganisation();
    }

    window.onload = function () {
        detectIdleSession();
    };

    /**
     * Get route query data and save to store
     */
    if (Object.keys(route.query).length) {
        const qsData = { ...route.query };
        checkoutStore.setQueryStringData(keysToCamelCase(qsData));
        checkoutStore.setOverrideInvalidQueryStringData([]); // reset
    } else {
        checkoutStore.setQueryStringData(null); // reset
        checkoutStore.setOverrideInvalidQueryStringData([]); // reset
    }
});
</script>
